import { decodeFilters, encodeFilters } from '@/lib/filterUtils'
import { DATE_ONLY_TYPES, DATE_TIME_TYPES } from '@/utils/clickhouseTypes'
import { z } from 'zod'
import { buildConditionId } from './groupedFilterFields'
import { buildGroupId } from './groupedFilterState'
import type { FilterGroup, FilterState } from './types'

/** The `ClickhouseToTSTypes` union, spelled as a value so the schema cannot drift from the type. */
const COLUMN_TYPES = [
	'int',
	'uint',
	'float',
	'string',
	'boolean',
	'null',
	...DATE_TIME_TYPES,
	...DATE_ONLY_TYPES,
] as const

const conditionSchema = z
	.object({
		id: z.string().optional(),
		field: z.string(),
		operator: z.string(),
		operatorLabel: z.string().optional(),
		value: z
			.union([z.array(z.union([z.string(), z.number(), z.null()])), z.string(), z.number(), z.null()])
			.default([]),
		type: z.enum(['metadata', 'state']).default('metadata'),
		isNullOperator: z.boolean().default(false),
		// Non-optional on FilterState, and the query builder reads it — but a legacy or hand-written link may
		// omit it or carry a type this build does not know. `catch` keeps such a condition instead of dropping
		// it, landing on the same 'string' `buildCondition` gives a field with no clickhouse type. Time windows
		// survive it: `isRelativeTimeCondition` still recognises `heartbeat` by field name.
		columnType: z.enum(COLUMN_TYPES).catch('string'),
	})
	.passthrough()

const groupSchema = z
	.object({
		id: z.string().optional(),
		operator: z.enum(['and', 'or']).default('and'),
		joiner: z.enum(['and', 'or']).default('or'),
		conditions: z.array(z.unknown()),
	})
	.passthrough()

/**
 * Shared `?groups` links already exist, so restore is lenient by construction: an unparseable condition
 * is dropped on its own and a group left empty goes with it, rather than the whole bar emptying.
 * `passthrough` keeps `fieldLabel` / `valueLabels` / `derivedExpression`, which the schema does not model
 * but the builder and chips read.
 */
export function parseGroupsParam(param: string | undefined): FilterGroup[] {
	const decoded = decodeFilters<unknown>(param)
	if (decoded === undefined) {
		return []
	}
	if (!Array.isArray(decoded)) {
		console.warn('Ignoring malformed `groups` URL parameter')
		return []
	}

	const groups: FilterGroup[] = []
	for (const raw of decoded) {
		const parsedGroup = groupSchema.safeParse(raw)
		if (!parsedGroup.success) {
			continue
		}
		const seen = new Set<string>()
		const conditions: FilterState[] = []
		for (const rawCondition of parsedGroup.data.conditions) {
			const parsed = conditionSchema.safeParse(rawCondition)
			if (!parsed.success) {
				continue
			}
			const id = parsed.data.id && !seen.has(parsed.data.id) ? parsed.data.id : buildConditionId()
			seen.add(id)
			conditions.push({ ...(parsed.data as unknown as FilterState), id })
		}
		if (conditions.length === 0) {
			continue
		}
		groups.push({
			id: parsedGroup.data.id ?? buildGroupId(),
			operator: parsedGroup.data.operator,
			joiner: parsedGroup.data.joiner,
			conditions,
		})
	}
	return groups
}

export const encodeGroupsParam = (groups: FilterGroup[]): string | undefined =>
	groups.length > 0 ? encodeFilters(groups) : undefined
